import React, { useRef, useState } from "react";
import { api, imageUrl, formatApiError } from "../lib/api";
import { UploadSimple, X, ImageSquare } from "@phosphor-icons/react";

export const ImageUpload = ({ value, onChange, testid = "image-upload" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      // IMPORTANT: do NOT manually set Content-Type here.
      // Browsers + axios auto-generate the correct
      //   "multipart/form-data; boundary=----WebKitFormBoundary..."
      // header from FormData. Setting it manually drops the boundary,
      // which makes some CDNs/proxies (Cloudflare in front of prod)
      // reject the request before it reaches FastAPI — surfacing as
      // a misleading 401 "Not authenticated".
      const { data } = await api.post("/upload", fd);
      onChange(data.path);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-file-input`}
      />
      {value ? (
        <div className="relative border border-[#2c3854] bg-[#070b14]">
          <img
            src={imageUrl(value)}
            alt="preview"
            className="w-full h-56 object-contain"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 bg-[#141e32] border border-[#f5ecd9] text-[0.7rem] font-mono-accent uppercase tracking-[0.18em] hover:bg-[#141e32] hover:text-white"
              data-testid={`${testid}-replace`}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="w-9 h-9 bg-[#141e32] border border-[#f5ecd9] flex items-center justify-center hover:bg-[#af3fa6] hover:text-white hover:border-[#af3fa6]"
              data-testid={`${testid}-remove`}
              aria-label="Remove image"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full h-56 border border-dashed border-[#f5ecd9] bg-[#141e32] flex flex-col items-center justify-center gap-3 hover:bg-[#141e32] hover:text-white transition-colors"
          data-testid={`${testid}-trigger`}
        >
          {uploading ? (
            <>
              <UploadSimple size={30} />
              <span className="font-mono-accent text-[0.72rem] uppercase tracking-[0.2em]">
                Uploading…
              </span>
            </>
          ) : (
            <>
              <ImageSquare size={30} />
              <span className="font-mono-accent text-[0.72rem] uppercase tracking-[0.2em]">
                Click to upload image
              </span>
              <span className="text-xs opacity-60">PNG, JPG, WEBP — up to 8MB</span>
            </>
          )}
        </button>
      )}
      {error && (
        <div className="mt-2 text-sm text-[#af3fa6]" data-testid={`${testid}-error`}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
